import { useRouterState } from "@tanstack/react-router";
import { FlaskConical } from "lucide-react";

/**
 * Modules whose screens all read the API when one is configured.
 *
 * `dashboard` (M12-6, #161) despite having only one section: its route is the
 * bare root (`/`, `config/modules.ts`'s dashboard entry has no path segment
 * of its own), so the `module` computed below is `""` there, not
 * `"dashboard"` — this entry documents intent and guards a future dashboard
 * sub-path, but `"/"` in `LIVE_SECTIONS` below is what actually hides the
 * badge on the one route the module has today.
 */
const LIVE_MODULES = new Set([
  "catalog",
  "orders",
  "contacts",
  "dashboard",
  "docs",
  "tasks",
]);

/**
 * Sections that are live inside a module that otherwise still renders
 * fixtures. Settings is the mixed case: Users, Roles, Groups, Saved Views,
 * Shopify Sync, Tags and the Audit Log read the API since #361, #332, #154,
 * #187, #405 and #190, while Company is still sample rows (#362). Inventory
 * and Warehouses are the same shape (M12-3): Stock Levels, Low Stock, stock
 * Adjustments (#171), the warehouse master, Transfers and In Transit (#172)
 * all read Postgres now — nothing left on fixtures for either module.
 * Reports (M12-6, #161) is the same again: Sales, Products and Inventory read
 * Postgres, GST stays on fixtures — GST is postponed, and it keeps its badge
 * until it is not. Exact paths, so a new section starts out badged until
 * someone wires it and adds it here.
 *
 * Contacts (M12-4), Docs (#381) and Tasks (#160) sit in `LIVE_MODULES`
 * instead: none of the three crosses the Shopify boundary (FR-CON-13,
 * FR-DOC, spec §8.2), so every one of their sections is real from the start,
 * with nothing left on fixtures for a later ticket to pick up.
 */
const LIVE_SECTIONS = new Set([
  "/", // dashboard → overview — see `LIVE_MODULES`'s comment on `dashboard`.
  "/settings",
  "/settings/roles",
  "/settings/groups",
  "/settings/views",
  "/settings/sync",
  "/settings/logistics",
  "/settings/tags",
  "/settings/audit",
  "/inventory",
  "/inventory/update-stock",
  "/warehouses",
  "/warehouses/transfers",
  "/warehouses/in-transit",
  "/reports",
  "/reports/products",
  "/reports/inventory",
]);

/**
 * A reminder that rows on screen are invented, shown only while they are.
 *
 * Fixed and small on purpose: it has to survive every screen without
 * competing with the data on it, but also never scroll out of view — the one
 * thing worse than a demo that looks unfinished is one that looks finished
 * and is quietly full of invented customers (#270 §3).
 *
 * Per module, not per app. It used to hide as soon as any API was configured,
 * so Orders read as live while it still showed fixture orders, and a random
 * fixture fulfilment time was taken for a real one (2026-09-15). A badge over
 * real data is the opposite failure, which is why live modules hide it.
 */
export function FixtureDataBadge() {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const module = pathname.split("/")[1] ?? "";
  const section = pathname.replace(/\/+$/, "") || "/";

  if (
    import.meta.env.VITE_API_URL &&
    (LIVE_MODULES.has(module) || LIVE_SECTIONS.has(section))
  ) {
    return null;
  }

  return (
    <div
      className={
        "bg-card text-muted-foreground pointer-events-none fixed right-3 " +
        "bottom-3 z-40 flex items-center gap-1.5 rounded-full border px-2.5 " +
        "py-1 text-[11px] font-medium shadow-sm " +
        // Above a phone's selection bar rather than over its Actions (#647).
        "max-md:[body:has([role=region][aria-label=Selection])_&]:bottom-20"
      }
    >
      <FlaskConical className="size-3" aria-hidden />
      Sample data — not real records
    </div>
  );
}
